import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface CandidateInfo {
  id: number;
  full_name: string;
  email: string;
  phone_number?: string;
  portfolio?: string;
  resume?: string;
  status: string;
  total_score: string;
  bucket: string;
  created_at: string;
  updated_at: string;
}

export interface AssessmentInfo {
  id: number;
  title: string;
  status: string;
  total_candidated_allowed: number;
  job_description: string;
  duration_in_minutes: number;
}

export interface AlignmentOverviewItem {
  category: string;
  verdict: string;
  comment: string;
}

export interface StrengthOrWatchout {
  title: string;
  badge: string;
  description: string;
}

export interface Suggestion {
  action: string;
  status: string | null;
  description: string;
}

export interface ReportSummaryData {
  response_details: CandidateInfo;
  alignment_overview: AlignmentOverviewItem[];
  top_strength_highlights: StrengthOrWatchout[];
  development_watchouts: StrengthOrWatchout[];
  suggestion: Suggestion;
}

interface ReportSummaryResponse {
  message: string;
  data: ReportSummaryData;
}

/**
 * Hook for fetching report summary (alignment overview, strengths, watchouts)
 */
export const useReportSummary = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reportSummary', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<ReportSummaryResponse>(
        `/reports/${reportId}/summary/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch report summary';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    summary: data,
    isLoading,
    error,
    refetch,
  };
};
